import React, { useContext } from 'react';
import * as pt from 'prop-types';
import { Badge } from '@thd-olt-component-react/badge';
import {
  params, shape, client, string, alias, useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import classNames from 'classnames';

const BADGE_MODELS = {
  new: 'New',
  seasonal: 'New This Season',
  exclusive: 'Exclusive',
  trending: 'Trending',
  bestseller: 'Best Seller',
  custom: 'Custom',
  guaranteed: 'Guaranteed',
  popular: 'Popular on Pinterest',
};

const INSPIRATIONAL_BADGE_MODELS = {
  new: 'New',
  trending: 'Trending',
  bestseller: 'Best Seller',
  exclusive: 'Exclusive',
};

const millisecondsPerDay = (1000 * 60 * 60 * 24);

function getBadge({ badges, inspirational }) {

  const productBadges = inspirational
    ? INSPIRATIONAL_BADGE_MODELS
    : BADGE_MODELS;
  const displayableBadges = Object.keys(productBadges);
  const itemBadges = badges
    .filter((badge) => displayableBadges.indexOf(badge.name) > -1);
  return itemBadges.length ? itemBadges[0] : null;
}

function getEventBadge(badges) {
  const itemBadges = badges
    .filter((badge) => { return badge.name === 'Event Badge'; });
  return itemBadges.length ? itemBadges[0] : null;
}

function getTimerThreshold(timerDuration) {
  if (!timerDuration || (timerDuration <= millisecondsPerDay)) {
    return '0';
  }
  return millisecondsPerDay;
}

function getDaysLeftThreshold(timerDuration) {
  if (!timerDuration || (timerDuration <= millisecondsPerDay)) {
    return '0';
  }
  return timerDuration;
}

function getEventTimerConfig(eventBadge) {
  return {
    endDate: eventBadge.endDate,
    timerThreshold: getTimerThreshold(eventBadge.timerDuration),
    daysLeftThreshold: getDaysLeftThreshold(eventBadge.timerDuration),
    dateDisplay: (eventBadge.timerDuration === null || eventBadge.timerDuration === '0'),
    dateDisplayThreshold: eventBadge.endDate && (+Date.parse(eventBadge.endDate) - Date.now() + 1)
  };
}

function shouldDisplayTimer(eventBadge, config) {
  const offset = eventBadge.timerDuration ? +eventBadge.timerDuration : 0;
  const endDateMilliseconds = eventBadge.endDate && Date.parse(eventBadge.endDate);
  const isBeforeEndDate = (Date.now() <= endDateMilliseconds);
  const isAfterThreshold = (Date.now() + offset >= endDateMilliseconds);
  const isEndDateDisplay = config && config.dateDisplay;
  return !!(isBeforeEndDate && (isAfterThreshold || isEndDateDisplay));
}

const ProductDetailsBadge = ({ storeId: propsStoreId, itemId, inspirational }) => {

  const { channel } = useContext(ExperienceContext);

  const storeId = propsStoreId || useStoreId();

  const opts = {
    ssr: false,
    variables: { itemId, storeId }
  };
  const { data, loading, error } = useDataModel('clientOnlyProduct', opts);
  if ((error && !data) || !data || loading) {
    return null;
  }

  const defaultBadge = (getBadge({ badges: data?.product?.badges || [], inspirational }));
  const eventBadge = (getEventBadge(data?.product?.badges || []));
  const timerConfig = eventBadge && getEventTimerConfig(eventBadge);
  const isMobileDisplayCrowded = (channel === 'mobile')
    && defaultBadge
    && eventBadge
    && timerConfig
    && shouldDisplayTimer(eventBadge, timerConfig);
  const wrapperClasses = classNames('product-details__badge-container-wrapper',
    { 'product-details__badge-container-wrapper--crowded': isMobileDisplayCrowded });

  if (!(defaultBadge || eventBadge)) {
    return null;
  }

  return (
    <span className={wrapperClasses}>
      { defaultBadge && (
        <span className="product-details__badge-container">
          <Badge
            type="text"
            text={defaultBadge.label}
            color="#3e7697"
          />
        </span>
      )}
      { eventBadge && (
        <span className="product-details__badge-container product-details__badge-container--event">
          {eventBadge.creativeImageUrl && (channel !== 'mobile') && (
            <img src={eventBadge.creativeImageUrl} alt="" />
          )}
          <Badge
            type="text"
            text={eventBadge.label}
            color={eventBadge.color || '#3e7697'}
          />
        </span>
      )}
      { eventBadge && timerConfig && shouldDisplayTimer(eventBadge, timerConfig)
        && (
          <span className="product-details__badge-container product-details__badge-container--timer">
            <CountdownTimer
              endDate={timerConfig.endDate}
              timebombThreshold={timerConfig.timerThreshold}
              shouldUseDaysLeftDisplay
              shouldUseClockDisplay
              shouldUseDateDisplay
              shouldUseMessagingString
              shouldIncludeSvg
              daysLeftThreshold={timerConfig.daysLeftThreshold}
              dateDisplayThreshold={timerConfig.dateDisplay && timerConfig.dateDisplayThreshold}
            />
          </span>
        )}
    </span>
  );
};

ProductDetailsBadge.propTypes = {
  itemId: pt.string.isRequired,
  inspirational: pt.bool,
  storeId: pt.oneOfType([pt.string, pt.number])
};

ProductDetailsBadge.defaultProps = {
  inspirational: false,
  storeId: null
};

ProductDetailsBadge.dataModel = {
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    badges: client(
      params({ storeId: string() }).arrayOf(shape({
        color: string(),
        creativeImageUrl: string(),
        endDate: string(),
        label: string(),
        message: string(),
        name: string(),
        timerDuration: string(),
      }))
    )
  })
};

export default ProductDetailsBadge;
